import { useCallback, useEffect, useRef, useState } from 'react';

interface HasCreatedAtAndTitle {
  created_at: string;
  title: string;
}

interface UseLoadMorePaginationResult<T> {
  items: T[];
  isLoading: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => Promise<void>;
}

/**
 * Generic Load More pagination hook.
 *
 * - Accepts a fetcher that returns items for a given page.
 * - Manages page, accumulation, hasMore flag and race-condition protection.
 * - Dedupe is based on `${title}-${created_at}` to match ActivityItem semantics.
 */
export function useLoadMorePagination<T extends HasCreatedAtAndTitle>(
  fetcher: (page: number) => Promise<T[]>,
  pageSize = 200,
): UseLoadMorePaginationResult<T> {
  const [items, setItems] = useState<T[]>([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const requestIdRef = useRef(0);

  const mergeItems = useCallback(
    (incoming: T[]) => {
      setItems((prev) => {
        if (page === 1) {
          return incoming;
        }
        const existingIds = new Set(prev.map((item) => `${item.title}-${item.created_at}`));
        const newItems = incoming.filter((item) => {
          return !existingIds.has(`${item.title}-${item.created_at}`);
        });
        return [...prev, ...newItems];
      });
      setHasMore(incoming.length === pageSize);
    },
    [page, pageSize],
  );

  // Initial load / reset when fetcher reference changes
  useEffect(() => {
    let isMounted = true;
    const currentRequestId = ++requestIdRef.current;

    setItems([]);
    setPage(1);
    setHasMore(true);
    setIsLoading(true);

    fetcher(1)
      .then((data) => {
        if (!isMounted || currentRequestId !== requestIdRef.current) return;
        mergeItems(data);
      })
      .finally(() => {
        if (!isMounted || currentRequestId !== requestIdRef.current) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
     
  }, [fetcher, pageSize]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore) return;

    const nextPage = page + 1;
    setLoadingMore(true);
    const currentRequestId = ++requestIdRef.current;

    try {
      const data = await fetcher(nextPage);
      if (currentRequestId !== requestIdRef.current) return;
      setPage(nextPage);
      mergeItems(data);
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoadingMore(false);
      }
    }
  }, [fetcher, hasMore, loadingMore, mergeItems, page]);

  return {
    items,
    isLoading,
    hasMore,
    loadingMore,
    loadMore,
  };
}
